import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useBoolean } from 'react-use';
import { ThemeContext } from 'styled-components';

import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { hasImageError, hasVideoError } from '@app/pages/Prototype/NftManagerPanel/utils';
import { addUnsupportedNft } from '@app/store/actions/nft-manager';
import { type NFTItem } from '@app/store/types/nft-manager';
import { type NFTContent } from '@app/store/types/yat-page-actions';

import { NftAsset, type NftAssetProps, NftDescription, Selectable, type SelectableProps } from './components';

import { NftCardWrapper } from './styles';

export const SESSION_STORAGE_KEY = 'nft-videos';
export type IsDisabledPageItem = (item: NFTItem, error?: boolean) => boolean;

export type NftCardProps = Pick<NftAssetProps, 'handleAddItem' | 'handleRemoveItem'> &
    Pick<SelectableProps, 'handleSelect' | 'handleIsSelected'> & {
        selected?: boolean;
        item: NFTItem;
        handleIsDisabledPageItem: IsDisabledPageItem;
    };

export function NftCard({
    item,
    selected = false,
    handleAddItem,
    handleRemoveItem,
    handleIsSelected,
    handleIsDisabledPageItem,
    handleSelect,
}: NftCardProps) {
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [hasAssetError, setHasAssetError] = useState(false);

    const unsupported = useAppSelector((state) => state.nftManager.managerFilters.unsupported);
    const liquidIDItems = useAppSelector((state) => state.yatPage.liquidIDItems);
    const editingLiquidID = useAppSelector((state) => state.yatPage.editStates.editingLiquidID);

    const liquidIDSelected = Boolean(
        editingLiquidID && liquidIDItems.some((liquidIdItem) => liquidIdItem && liquidIdItem.id == item.id),
    );

    const { content } = item;

    const handleAssetError = useCallback(
        (hasError: boolean) => {
            setIsLoading(false);
            setHasAssetError(hasError);
            if (hasError && editingLiquidID) dispatch(addUnsupportedNft(item.id));
        },
        [dispatch, item.id, editingLiquidID],
    );

    useEffect(() => {
        if (!content) return;
        if (content.video && !content.image) {
            hasVideoError(SESSION_STORAGE_KEY, content.video).then(handleAssetError);
        } else {
            hasImageError(content.image).then(handleAssetError);
        }
    }, [handleAssetError, content]);

    const theme = useContext(ThemeContext);

    const [error, setError] = useBoolean(false);

    const disabled = handleIsDisabledPageItem(item, error);
    if (!unsupported && disabled && editingLiquidID) {
        return null;
    }

    const isDisabled = editingLiquidID ? disabled || error : error;

    return hasAssetError ? null : (
        <Selectable item={item} disabled={isDisabled} handleIsSelected={handleIsSelected} handleSelect={handleSelect}>
            <NftCardWrapper
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: theme.transitions.speeds.fast }}
            >
                <NftAsset
                    handleAddItem={handleAddItem}
                    handleRemoveItem={handleRemoveItem}
                    preloading={isLoading}
                    item={item}
                    disabled={disabled}
                    editingLiquidID={!!editingLiquidID}
                    liquidIDSelected={liquidIDSelected}
                    error={error}
                    onError={setError}
                />

                <NftDescription content={item.content as NFTContent} disabled={disabled || error} selected={selected} />
            </NftCardWrapper>
        </Selectable>
    );
}
